import { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Float, Text, Box, Cylinder, Sphere } from '@react-three/drei';
import * as THREE from 'three';

// Abacus bead
function Bead({ position, color, active }: { position: [number, number, number]; color: string; active?: boolean }) {
  const beadRef = useRef<THREE.Mesh>(null);
  
  useFrame((state) => {
    if (beadRef.current && active) {
      beadRef.current.rotation.y = state.clock.elapsedTime;
    }
  });
  
  return (
    <mesh ref={beadRef} position={position}>
      <Cylinder args={[0.12, 0.12, 0.15, 16]} rotation={[0, 0, Math.PI / 2]}>
        <meshStandardMaterial color={color} />
      </Cylinder>
      <Sphere args={[0.12]} position={[-0.075, 0, 0]}>
        <meshStandardMaterial color={color} />
      </Sphere>
      <Sphere args={[0.12]} position={[0.075, 0, 0]}>
        <meshStandardMaterial color={color} />
      </Sphere>
    </mesh>
  );
}

// Abacus rod
function Rod({ position }: { position: [number, number, number] }) {
  return (
    <Cylinder args={[0.015, 0.015, 2.5]} position={position}>
      <meshStandardMaterial color="#8B4513" />
    </Cylinder>
  );
}

// Abacus frame
function Frame() {
  return (
    <group>
      {/* Top bar */}
      <Box args={[3.5, 0.15, 0.3]} position={[0, 1.3, 0]}>
        <meshStandardMaterial color="#5D4037" />
      </Box>
      {/* Bottom bar */}
      <Box args={[3.5, 0.15, 0.3]} position={[0, -1.3, 0]}>
        <meshStandardMaterial color="#5D4037" />
      </Box>
      {/* Divider */}
      <Box args={[3.5, 0.08, 0.25]} position={[0, 0.3, 0]}>
        <meshStandardMaterial color="#4A3728" />
      </Box>
      {/* Left side */}
      <Box args={[0.15, 2.9, 0.3]} position={[-1.8, 0, 0]}>
        <meshStandardMaterial color="#5D4037" />
      </Box>
      {/* Right side */}
      <Box args={[0.15, 2.9, 0.3]} position={[1.8, 0, 0]}>
        <meshStandardMaterial color="#5D4037" />
      </Box>
    </group>
  );
}

// Complete abacus
function Abacus() {
  const rods = 9;
  const rodSpacing = 0.35;
  
  // Bead positions for each rod
  const beadConfigs = useMemo(() => {
    const configs: { position: [number, number, number]; color: string; active: boolean }[] = [];
    
    for (let rod = 0; rod < rods; rod++) {
      const x = (rod - rods / 2 + 0.5) * rodSpacing;
      
      // Upper deck beads (2 beads)
      for (let i = 0; i < 2; i++) {
        configs.push({
          position: [x, 0.9 - i * 0.3, 0],
          color: '#F97316',
          active: rod === 4 && i === 0
        });
      }
      
      // Lower deck beads (5 beads)
      for (let i = 0; i < 5; i++) {
        configs.push({
          position: [x, -0.2 - i * 0.25, 0],
          color: '#3B82F6',
          active: rod === 4 && i < 3
        });
      }
    }
    return configs;
  }, []);
  
  return (
    <group>
      <Frame />
      {/* Rods */}
      {[...Array(rods)].map((_, i) => (
        <Rod 
          key={i} 
          position={[(i - rods / 2 + 0.5) * rodSpacing, 0, 0]} 
        />
      ))}
      {/* Beads */}
      {beadConfigs.map((config, i) => (
        <Bead key={i} {...config} />
      ))}
    </group>
  );
}

// Floating numbers
function FloatingNumbers() {
  const numbers = ['1', '2', '3', '+', '-', '×', '÷', '='];
  
  return (
    <>
      {numbers.map((num, i) => (
        <Float
          key={i}
          speed={1.5}
          rotationIntensity={0.3}
          floatIntensity={0.3}
          position={[
            Math.cos((i / numbers.length) * Math.PI * 2) * 2.5,
            Math.sin((i / numbers.length) * Math.PI * 2) * 0.8,
            Math.sin((i / numbers.length) * Math.PI * 2) * 0.5
          ]}
        >
          <Text
            fontSize={0.25}
            color="#F97316"
            anchorX="center"
            anchorY="middle"
            font="https://fonts.gstatic.com/s/inter/v12/UcCO3FwrK3iLTeHuS_fvQtMwCp50KnMw2boKoduKmMEVuGKYAZ9hjp-Ek-_EeA.woff2"
          >
            {num}
          </Text>
        </Float>
      ))}
    </>
  );
}

// Particles
function Particles() {
  const count = 25;
  const positions = useMemo(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 6;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 4;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 2;
    }
    return pos;
  }, []);

  const pointsRef = useRef<THREE.Points>(null);

  useFrame((state) => {
    if (pointsRef.current) {
      pointsRef.current.rotation.y = state.clock.elapsedTime * 0.02;
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={count}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.04}
        color="#F97316"
        transparent
        opacity={0.5}
        sizeAttenuation
      />
    </points>
  );
}

// Main scene
function Scene() {
  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[5, 5, 5]} intensity={0.8} />
      <pointLight position={[-5, 3, -5]} intensity={0.5} color="#F97316" />
      <pointLight position={[5, 3, 5]} intensity={0.4} color="#3b82f6" />
      
      <Float speed={1.5} rotationIntensity={0.05} floatIntensity={0.1}>
        <group position={[0, 0, 0]}>
          <Abacus />
        </group>
      </Float>
      
      <FloatingNumbers />
      <Particles />
      
      <OrbitControls 
        enableZoom={false} 
        enablePan={false}
        maxPolarAngle={Math.PI / 2}
        minPolarAngle={Math.PI / 3}
        autoRotate
        autoRotateSpeed={0.2}
      />
    </>
  );
}

export default function Abacus3D() {
  return (
    <div className="w-full h-[400px] md:h-[500px]">
      <Canvas camera={{ position: [0, 0, 5], fov: 50 }} gl={{ antialias: true }}>
        <Scene />
      </Canvas>
    </div>
  );
}